import ContentLayout from "@/components/layout/ContentLayout"
import { FC, useEffect } from "react"
import { Card, Row, Col, Typography, Button, Space } from "antd"
import { MessageOutlined, QuestionCircleOutlined, BookOutlined, VideoCameraOutlined } from "@ant-design/icons"
import { crispUtils } from "@/components/crisp-chat"

const { Title, Paragraph } = Typography

const HelpCenter: FC = () => {
  useEffect(() => {
    // Track help center visit
    crispUtils.trackEvent('help_center_visit', {
      page: 'help_center_main',
      timestamp: new Date().toISOString()
    })

    // Auto-open chat with helpful message after a short delay
    const timer = setTimeout(() => {
      crispUtils.openChatWithMessage(
        "👋 Welcome to the Help Center! I'm your AI assistant and I'm here to help you with anything - from setting up integrations to troubleshooting issues. What can I help you with today?"
      )
    }, 2000)

    return () => clearTimeout(timer)
  }, [])

  const handleQuickHelp = (topic: string) => {
    const messages = {
      'integration': "I'd like help setting up a new integration",
      'troubleshooting': "I'm having issues with an existing integration",
      'api': "I need help with API configuration and authentication",
      'mapping': "I need assistance with data field mapping",
      'billing': "I have questions about pricing and billing",
      'account': "I need help with my account settings"
    }
    
    crispUtils.trackEvent('quick_help_selected', { topic })
    crispUtils.openChatWithMessage(messages[topic as keyof typeof messages] || "I need help with my account")
  }

  return (
    <ContentLayout title={"Help Center"}>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* AI Chat Prominence */}
        <Card style={{ marginBottom: '24px', background: 'linear-gradient(135deg, #1677ff 0%, #69b1ff 100%)', border: 'none' }}>
          <Row align="middle" justify="space-between">
            <Col span={16}>
              <Title level={3} style={{ color: 'white', margin: 0 }}>
                <MessageOutlined style={{ marginRight: '12px' }} />
                Get Instant Help with AI
              </Title>
              <Paragraph style={{ color: 'rgba(255,255,255,0.9)', marginBottom: 0, fontSize: '16px' }}>
                Our AI assistant can help you solve most issues instantly, guide you through integrations, and provide step-by-step assistance.
              </Paragraph>
            </Col>
            <Col>
              <Button 
                type="primary" 
                size="large" 
                style={{ background: 'white', color: '#1677ff', border: 'none', fontWeight: 600 }}
                onClick={() => handleQuickHelp('integration')}
              >
                Start Chat
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Quick Help Topics */}
        <Title level={3}>Quick Help Topics</Title>
        <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
          <Col xs={24} sm={12} md={8}>
            <Card 
              hoverable 
              onClick={() => handleQuickHelp('integration')}
              style={{ height: '100%' }}
            >
              <QuestionCircleOutlined style={{ fontSize: '24px', color: '#1677ff', marginBottom: '12px' }} />
              <Title level={4}>Setting Up Integrations</Title>
              <Paragraph>Get help connecting your data sources, APIs, and configuring authentication.</Paragraph>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card 
              hoverable 
              onClick={() => handleQuickHelp('troubleshooting')}
              style={{ height: '100%' }}
            >
              <BookOutlined style={{ fontSize: '24px', color: '#1677ff', marginBottom: '12px' }} />
              <Title level={4}>Troubleshooting Issues</Title>
              <Paragraph>Resolve connection problems, errors, and performance issues quickly.</Paragraph>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card 
              hoverable 
              onClick={() => handleQuickHelp('mapping')}
              style={{ height: '100%' }}
            >
              <VideoCameraOutlined style={{ fontSize: '24px', color: '#1677ff', marginBottom: '12px' }} />
              <Title level={4}>Data Mapping Help</Title>
              <Paragraph>Learn how to map fields, transform data, and set up custom rules.</Paragraph>
            </Card>
          </Col>
        </Row>

        {/* Common Questions */}
        <Title level={3}>Frequently Asked Questions</Title>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Card>
            <Title level={5}>How do I connect my Salesforce account?</Title>
            <Paragraph>
              Click here to get step-by-step guidance from our AI assistant.
              <Button 
                type="link" 
                onClick={() => handleQuickHelp('api')}
                style={{ padding: '0 8px' }}
              >
                Get Help
              </Button>
            </Paragraph>
          </Card>
          
          <Card>
            <Title level={5}>Why is my integration failing?</Title>
            <Paragraph>
              Our AI can diagnose common integration issues and provide solutions.
              <Button 
                type="link" 
                onClick={() => handleQuickHelp('troubleshooting')}
                style={{ padding: '0 8px' }}
              >
                Troubleshoot Now
              </Button>
            </Paragraph>
          </Card>

          <Card>
            <Title level={5}>How do I modify field mappings?</Title>
            <Paragraph>
              Get instant help with data mapping and transformation rules.
              <Button 
                type="link" 
                onClick={() => handleQuickHelp('mapping')}
                style={{ padding: '0 8px' }}
              >
                Learn More
              </Button>
            </Paragraph>
          </Card>
        </Space>

        {/* Contact Options */}
        <Card style={{ marginTop: '32px' }}>
          <Title level={4}>Need More Help?</Title>
          <Paragraph>
            Our AI assistant resolves 80% of questions instantly. For complex issues or if you're a premium user, 
            you can escalate to our human support team directly through the chat.
          </Paragraph>
          <Button 
            type="primary" 
            icon={<MessageOutlined />}
            onClick={() => crispUtils.showChat()}
          >
            Contact Support
          </Button>
        </Card>
      </div>
    </ContentLayout>
  )
}

export default HelpCenter
